/**
 * Product analytics (PostHog). One thin wrapper so call sites never import
 * the SDK and analytics can never throw into app code.
 *
 * What we send: pageviews, the four funnel steps (visit → humanize → sign up
 * → pay), the detector, and CTA clicks. What we don't: session recordings,
 * autocaptured clicks, or any document text. `?ref=CODE` in the landing URL
 * is stuck to the visitor as a super property + person property so an
 * ambassador's sign-ups and payments can be attributed to their code; UTM
 * parameters are picked up by the SDK on its own.
 *
 * No key (dev builds) ⇒ every function is a no-op.
 */
import posthog from "posthog-js";

const KEY = import.meta.env.VITE_POSTHOG_KEY ?? "";
const HOST = import.meta.env.VITE_POSTHOG_HOST ?? "https://us.i.posthog.com";

let ready = false;

export function initAnalytics(): void {
  if (!KEY || ready) return;
  try {
    posthog.init(KEY, {
      api_host: HOST,
      // localStorage, not a cookie — identity survives reloads without a
      // cookie banner; still no cross-site tracking.
      persistence: "localStorage",
      disable_session_recording: true,
      autocapture: false,
      capture_pageview: true,
      capture_pageleave: true,
    });
    const ref = new URLSearchParams(window.location.search).get("ref");
    if (ref) {
      posthog.register({ ref });
      posthog.setPersonProperties({ ref_last: ref }, { ref_first: ref });
    }
    ready = true;
  } catch {
    // Analytics must never break the app.
  }
}

export function track(event: string, props?: Record<string, unknown>): void {
  if (!ready) return;
  try {
    posthog.capture(event, props);
  } catch {
    // ignore
  }
}

/** Like `track`, flushed immediately — for clicks that navigate away. */
export function trackNow(event: string, props?: Record<string, unknown>): void {
  if (!ready) return;
  try {
    posthog.capture(event, props, { send_instantly: true });
  } catch {
    // ignore
  }
}

/** Tie the anonymous visitor to an account (the email is the stable id). */
export function identify(id: string, props?: Record<string, unknown>): void {
  if (!ready) return;
  try {
    posthog.identify(id, props);
  } catch {
    // ignore
  }
}

/** Forget the identity on logout so the next visitor is not the last user. */
export function resetAnalytics(): void {
  if (!ready) return;
  try {
    posthog.reset();
  } catch {
    // ignore
  }
}
